import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

// Logo URLs - CloudGM Business (Light/Dark variants)
const LOGO_LIGHT = "/logo-light.png"; // Black text for light theme
const LOGO_DARK = "/logo-dark.png";   // White text for dark theme

// Company Information
export const COMPANY_INFO = {
  email: 'support@cloudgm.business',
  phone: '+971501511917',
  phoneDisplay: '+971 50 151 1917',
  address: {
    ar: 'الإمارات العربية المتحدة، إمارة رأس الخيمة، راكز',
    en: 'United Arab Emirates, Ras Al Khaimah, RAKEZ'
  },
  website: 'cloudgm.business',
  socialMedia: {
    facebook: '#',
    twitter: '#',
    instagram: '#',
    linkedin: '#'
  }
};

export default function Footer() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();

  const quickLinks = [
    { name: language === 'ar' ? 'الرئيسية' : 'Home', path: '/' },
    { name: language === 'ar' ? 'من نحن' : 'About Us', path: '/about-us' },
    { name: language === 'ar' ? 'الدعم والتواصل' : 'Contact & Support', path: '/contact-us' },
    { name: language === 'ar' ? 'تسجيل الدخول' : 'Login', path: '/login' },
    { name: language === 'ar' ? 'إنشاء حساب' : 'Register', path: '/register' }
  ];

  const legalLinks = [
    { name: language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy', path: '/privacy-policy' },
    { name: language === 'ar' ? 'شروط الخدمة' : 'Terms of Service', path: '/terms-of-service' },
    { name: language === 'ar' ? 'حذف بيانات المستخدم' : 'User Data Deletion', path: '/user-data-deletion' }
  ];

  const serviceLinks = [
    { name: language === 'ar' ? 'متاجري' : 'My Stores', path: '/stores' },
    { name: language === 'ar' ? 'لوحة التحكم' : 'Dashboard', path: '/dashboard' },
    { name: language === 'ar' ? 'كن وكيلاً' : 'Become an Agent', path: '/become-agent' }
  ];

  return (
    <footer className="relative overflow-x-clip">
      {/* Base — always-dark elegant background with brand gradient top border */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A] via-[#0B1120] to-[#060910]" />
      {/* Top accent bar — brand gradient (sky → orange, matches logo) */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#38BDF8] via-[#38BDF8] to-[#F97316]" />
      
      {/* Gradient Glow Overlay — brand colours only, pointer-events-none */}
      <div className="absolute inset-0 opacity-25 pointer-events-none overflow-hidden">
        <div className="absolute top-10 start-1/4 w-96 h-96 rounded-full bg-[#38BDF8]/30 blur-[100px]" />
        <div className="absolute bottom-10 end-1/4 w-96 h-96 rounded-full bg-[#F97316]/30 blur-[100px]" />
      </div>
      
      {/* Main Footer */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <img 
                src={LOGO_DARK} 
                alt="CloudGM Business" 
                className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {language === 'ar' 
                ? 'منصة التجار الشاملة لإنشاء وإدارة المتاجر الإلكترونية في الإمارات العربية المتحدة'
                : 'The comprehensive merchant platform for creating and managing online stores in the UAE'}
            </p>
            {/* Social Media */}
            <div className="flex items-center gap-3">
              <a 
                href={COMPANY_INFO.socialMedia.facebook} 
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#38BDF8] hover:to-[#F97316] transition-all duration-300 group" 
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a 
                href={COMPANY_INFO.socialMedia.twitter} 
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#38BDF8] hover:to-[#F97316] transition-all duration-300 group" 
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a 
                href={COMPANY_INFO.socialMedia.instagram} 
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#38BDF8] hover:to-[#F97316] transition-all duration-300 group" 
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a 
                href={COMPANY_INFO.socialMedia.linkedin} 
                className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-gradient-to-r hover:from-[#38BDF8] hover:to-[#F97316] transition-all duration-300 group" 
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-white">
              {language === 'ar' ? 'روابط سريعة' : 'Quick Links'}
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-[#38BDF8] transition-colors text-sm inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-white">
              {language === 'ar' ? 'خدماتنا' : 'Our Services'}
            </h3>
            <ul className="space-y-3">
              {serviceLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-[#38BDF8] transition-colors text-sm inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-[#38BDF8] transition-colors text-sm inline-block"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4 text-white">
              {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
            </h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`mailto:${COMPANY_INFO.email}`} 
                  className="flex items-start gap-3 text-gray-400 hover:text-[#38BDF8] transition-colors group"
                >
                  <div className="p-2 rounded-lg bg-white/5 group-hover:bg-[#38BDF8]/10 transition-colors">
                    <Mail className="h-4 w-4" />
                  </div>
                  <span className="text-sm mt-1">{COMPANY_INFO.email}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`tel:${COMPANY_INFO.phone}`} 
                  className="flex items-start gap-3 text-gray-400 hover:text-[#38BDF8] transition-colors group" 
                  dir="ltr"
                >
                  <div className="p-2 rounded-lg bg-white/5 group-hover:bg-[#38BDF8]/10 transition-colors">
                    <Phone className="h-4 w-4" />
                  </div>
                  <span className="text-sm mt-1">{COMPANY_INFO.phoneDisplay}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-gray-400">
                  <div className="p-2 rounded-lg bg-white/5">
                    <MapPin className="h-4 w-4" />
                  </div>
                  <span className="text-sm mt-1">
                    {language === 'ar' ? COMPANY_INFO.address.ar : COMPANY_INFO.address.en}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm text-center sm:text-start">
              {language === 'ar' 
                ? `© ${new Date().getFullYear()} CloudGM Business. جميع الحقوق محفوظة.`
                : `© ${new Date().getFullYear()} CloudGM Business. All rights reserved.`}
            </p>
            <div className="flex items-center gap-4">
              <Link 
                to="/privacy-policy" 
                className="text-gray-500 hover:text-[#38BDF8] text-sm transition-colors"
              >
                {language === 'ar' ? 'الخصوصية' : 'Privacy'}
              </Link>
              <span className="text-gray-700">|</span>
              <Link 
                to="/terms-of-service" 
                className="text-gray-500 hover:text-[#38BDF8] text-sm transition-colors"
              >
                {language === 'ar' ? 'الشروط' : 'Terms'}
              </Link>
              <span className="text-gray-700">|</span>
              <Link 
                to="/user-data-deletion" 
                className="text-gray-500 hover:text-[#38BDF8] text-sm transition-colors"
              >
                {language === 'ar' ? 'حذف البيانات' : 'Data Deletion'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
